import { cva, type VariantProps } from 'class-variance-authority';

export type TextColor =
  | 'default'
  | 'heading'
  | 'white'
  | 'subtext'
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'error'
  | 'neutral';

/** Every public `color` value, including the deprecated `neutral` alias. */
export const TEXT_COLORS: readonly TextColor[] = [
  'default',
  'heading',
  'white',
  'subtext',
  'primary',
  'secondary',
  'accent',
  'error',
  'neutral',
];

export const textVariants = cva('', {
  variants: {
    variant: {
      d1: 'font-heading text-d1 leading-d1',
      d2: 'font-heading text-d2 leading-d2',
      d3: 'font-heading text-d3 leading-d3',
      b1: 'font-body text-b1 leading-b1',
      b2: 'font-body text-b2 leading-b2',
      b3: 'font-body text-b3 leading-b3',
      b4: 'font-body text-b4 leading-b4',
      b5: 'font-body text-b5 leading-b5',
      l1: 'font-body text-l1 leading-l1',
      l2: 'font-body text-l2 leading-l2',
      caption: 'font-body text-caption leading-caption',
    },
    color: {
      default: 'text-body',
      heading: 'text-heading',
      white: 'text-white',
      subtext: 'text-subtext',
      primary: 'text-primary-700',
      secondary: 'text-secondary-700',
      accent: 'text-text-accent',
      error: 'text-error-500',
      // @deprecated — `neutral` was renamed to `subtext` (EDUN-12023).
      // Retained as a non-breaking alias mapping to the same token so existing
      // downstream consumers continue to render correctly. Migrate to
      // `color="subtext"`; this entry will be removed in the next major.
      neutral: 'text-subtext',
    } satisfies Record<TextColor, string>,
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    weight: {
      light: 'font-light',
      regular: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    visuallyHidden: {
      true: 'sr-only',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'b4',
    color: 'default',
    weight: 'regular',
    visuallyHidden: false,
  },
});

export type TextVariantProps = VariantProps<typeof textVariants>;
